import { Box, IconButton, Stack, Tooltip } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { t } from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdArrowBack, MdExpandMore } from 'react-icons/md'
import { toast } from 'react-toastify'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import { MonthShortName } from 'src/enums'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { invalidateMainComponentCache, mainEndpoints, useBringBackMainComponent } from '../../../api/main-component'
import { invalidateMainComponentTypeCache } from '../../../api/main-component-type'
import GenericTable from '../../../components/table/GenericTable'

export const DiscardedMainComponentList: React.FC<{
  customerId: EntityId
  search?: string
}> = ({ customerId, search }) => {
  const { t: translate } = useTranslation(['main-component', 'common'])
  const { mutateAsync } = useBringBackMainComponent(customerId as EntityId)
  const { showConfirmDialog } = useDialogCtx()

  const bringBack = row => {
    showConfirmDialog(
      translate('bringBackMainComponent'),
      translate('areYouSureBringBackMainComponent'),
      { acceptText: t('yes', { ns: 'common' }), cancelText: t('no', { ns: 'common' }) },
      async pop => {
        await mutateAsync({ id: row.id }).then(() => {
          toast(translate('mainComponentBringBack'), { type: 'success' })
          invalidateMainComponentCache.getDiscardedMainComponents(customerId)
          invalidateMainComponentTypeCache.useGetMainComponentTypeWithMainComponentCount(customerId)
          invalidateMainComponentCache.getMainComponentsByComponentType(customerId, row.typeId)
          invalidateMainComponentCache.getMaincomponentsBySearch(customerId)
          pop()
        })
      },
    )
  }

  const STATIC_COLUMNS: GridColumns = [
    { field: 'serialNumber', headerName: translate('serialNumber', { ns: 'main-component' }), flex: 1 },
    {
      field: 'description',
      headerName: translate('description', { ns: 'common' }),
      flex: 2,
      renderCell: params => {
        return (
          <>
            <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
              <Tooltip title={params.value}>
                <IconButton
                  disabled
                  sx={{
                    padding: 0,
                    '&.Mui-disabled': {
                      pointerEvents: 'all',
                    },
                  }}
                >
                  <MdExpandMore />
                </IconButton>
              </Tooltip>
              {params.value}
            </Stack>
          </>
        )
      },
    },
    { field: 'orderNumber', headerName: translate('orderNumber', { ns: 'main-component' }), flex: 1 },
    {
      field: 'dateProduced',
      headerName: translate('dateProduced', { ns: 'main-component' }),
      flex: 1,
      renderCell: ({ row }) => {
        const producedYear = row?.productionYear != null ? row?.productionYear : '-'
        const producedMonth =
          row?.productionYear != null && row?.productionMonth != null ? '-' + MonthShortName[row?.productionMonth] : ''
        const dateProduced = producedYear + producedMonth
        return <DynamicTextTypography text={dateProduced} variant="body1" maxLength={10} fontWeight={500} />
      },
    },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        headerName: t('actions', { ns: 'common' }),
        type: 'actions' as GridNativeColTypes,
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            key={id}
            label={t('bring-back', { ns: 'common' })}
            icon={<MdArrowBack size={24} />}
            onClick={() => bringBack(row)}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [],
  )

  return (
    <Box sx={{ p: 5, pt: '68px' }} height={window.innerHeight - 170 + 'px'}>
      <GenericTable
        id={'discarded-main-components'}
        columns={columns}
        defaultColumnOption={columns.map(c => ({ field: c.field, headerName: c.headerName as string }))}
        enableColumnOptions={true}
        queryUrl={mainEndpoints.getDiscardedMainComponents(customerId as EntityId)}
        enableServerPagination={true}
        queryParams={{ search }}
        autoHeight={false}
        height={window.innerHeight - 220 + 'px'}
        noDataComponent={() => {
          return <EmptyStatusCard imageWidth="500px" />
        }}
      />
    </Box>
  )
}
